import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  InputNumber,
  Select,
  message,
  Popconfirm,
  Space,
  Card,
  Tag,
  DatePicker,
  Input,
  Divider,
  Row,
  Col,
  Statistic,
  Descriptions,
  List,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, DollarOutlined, PrinterOutlined, CheckCircleOutlined } from '@ant-design/icons';
import api from '../services/api';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';

const { TextArea } = Input;
const { Option } = Select;

const TIPOS_BONIFICACION = [
  'PROTOCOLO DE ODDO',
  'CUMPLIMIENTO DEL PROTOCOLO',
  'BONO DE LIMPIEZA DE GRUA',
  'BONO CUIDADO DE HERRAMIENTAS',
  'BONO DE PRODUCCION',
  'FERIADO',
  'BONO EXTRAORDINARIO',
  'HORAS EXTRA 25%',
  'HORAS EXTRA 35%',
];

const LiquidacionPagos = () => {
  const { user } = useAuth();
  const [liquidaciones, setLiquidaciones] = useState([]);
  const [personal, setPersonal] = useState([]);
  const [loading, setLoading] = useState(false);

  // Verificar si es admin principal (sin empresa_id)
  const isMainAdmin = user && user.rol === 'admin' && !user.empresa_id;
  const [modalVisible, setModalVisible] = useState(false);
  const [boletaModalVisible, setBoletaModalVisible] = useState(false);
  const [editingLiquidacion, setEditingLiquidacion] = useState(null);
  const [selectedLiquidacion, setSelectedLiquidacion] = useState(null);
  const [calculando, setCalculando] = useState(false);
  const [datosCalculo, setDatosCalculo] = useState(null);
  const [bonificaciones, setBonificaciones] = useState([]);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchLiquidaciones();
    fetchPersonal();
  }, []);

  const fetchLiquidaciones = async () => {
    setLoading(true);
    try {
      const response = await api.get('/liquidacion-pagos');
      setLiquidaciones(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      message.error('Error al cargar liquidaciones');
      setLiquidaciones([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPersonal = async () => {
    try {
      const response = await api.get('/personal');
      setPersonal(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error al cargar personal');
    }
  };

  const handleCalcular = async (personalId, mes, anio) => {
    setCalculando(true);
    try {
      const response = await api.get(`/liquidacion-pagos/calcular/${personalId}?mes=${mes}&anio=${anio}`);
      setDatosCalculo(response.data);
      form.setFieldsValue({
        personal_id: personalId,
        periodo_mes: mes,
        periodo_anio: anio,
        monto_base: response.data.monto_base,
        monto_bonificaciones: 0,
        monto_descuentos: 0,
        fecha_liquidacion: dayjs(),
      });
      setBonificaciones([]);
      message.success('Cálculo realizado exitosamente');
    } catch (error) {
      message.error(error.message);
    } finally {
      setCalculando(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      const data = {
        ...values,
        periodo_mes: parseInt(values.periodo_mes),
        periodo_anio: parseInt(values.periodo_anio),
        fecha_liquidacion: values.fecha_liquidacion.format('YYYY-MM-DD'),
        detalles: datosCalculo?.detalles || [],
        bonificaciones: bonificaciones,
        monto_bonificaciones: bonificaciones.reduce((sum, b) => sum + parseFloat(b.monto || 0), 0),
      };

      if (editingLiquidacion) {
        await api.put(`/liquidacion-pagos/${editingLiquidacion.id}`, data);
        message.success('Liquidación actualizada exitosamente');
      } else {
        const response = await api.post('/liquidacion-pagos', data);
        message.success('Liquidación creada exitosamente');
        if (response.data.numero_boleta) {
          message.info(`Número de boleta: ${response.data.numero_boleta}`);
        }
      }
      setModalVisible(false);
      form.resetFields();
      setEditingLiquidacion(null);
      setDatosCalculo(null);
      setBonificaciones([]);
      fetchLiquidaciones();
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleEdit = async (record) => {
    try {
      const response = await api.get(`/liquidacion-pagos/${record.id}`);
      const liquidacion = response.data;

      if (!liquidacion || !liquidacion.id) {
        message.error('No se encontraron datos de la liquidación');
        return;
      }

      setEditingLiquidacion(liquidacion);
      form.setFieldsValue({
        personal_id: liquidacion.personal_id,
        periodo_mes: liquidacion.periodo_mes,
        periodo_anio: liquidacion.periodo_anio,
        monto_base: liquidacion.monto_base,
        monto_bonificaciones: liquidacion.monto_bonificaciones || 0,
        monto_descuentos: liquidacion.monto_descuentos || 0,
        fecha_liquidacion: liquidacion.fecha_liquidacion ? dayjs(liquidacion.fecha_liquidacion) : dayjs(),
        fecha_pago: liquidacion.fecha_pago ? dayjs(liquidacion.fecha_pago) : null,
        estado: liquidacion.estado,
        observaciones: liquidacion.observaciones,
      });
      setBonificaciones(liquidacion.bonificaciones || []);

      // Cargar datos de cálculo si hay detalles
      if (liquidacion.detalles && liquidacion.detalles.length > 0) {
        setDatosCalculo({
          personal: {
            tipo_pago: liquidacion.tipo_pago,
          },
          monto_base: liquidacion.monto_base,
          detalles: liquidacion.detalles,
        });
      }

      setModalVisible(true);
    } catch (error) {
      console.error('Error al cargar liquidación:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error desconocido';
      message.error('Error al cargar liquidación: ' + errorMessage);
    }
  };

  const handleVerBoleta = async (record) => {
    try {
      const response = await api.get(`/liquidacion-pagos/${record.id}`);
      const liquidacion = response.data;

      if (!liquidacion || !liquidacion.id) {
        message.error('No se encontraron datos de la boleta');
        return;
      }

      setSelectedLiquidacion(liquidacion);
      setBoletaModalVisible(true);
    } catch (error) {
      console.error('Error al cargar boleta:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error desconocido';
      message.error('Error al cargar boleta: ' + errorMessage);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/liquidacion-pagos/${id}`);
      message.success('Liquidación eliminada exitosamente');
      fetchLiquidaciones();
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleAprobar = async (record) => {
    try {
      await api.put(`/liquidacion-pagos/${record.id}`, {
        estado: 'pagado',
        fecha_pago: dayjs().format('YYYY-MM-DD'),
      });
      message.success('Liquidación aprobada y cerrada exitosamente');
      fetchLiquidaciones();
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleAgregarBonificacion = () => {
    setBonificaciones([...bonificaciones, { descripcion: '', monto: 0 }]);
  };

  const handleEliminarBonificacion = (index) => {
    setBonificaciones(bonificaciones.filter((_, i) => i !== index));
  };

  const handleBonificacionChange = (index, field, value) => {
    const nuevas = [...bonificaciones];
    nuevas[index][field] = value;
    setBonificaciones(nuevas);

    // Actualizar monto total de bonificaciones en el form
    const totalBonificaciones = nuevas.reduce((sum, b) => sum + parseFloat(b.monto || 0), 0);
    form.setFieldsValue({ monto_bonificaciones: totalBonificaciones });
  };

  const columns = [
    {
      title: 'Número Boleta',
      dataIndex: 'numero_boleta',
      key: 'numero_boleta',
    },
    {
      title: 'Personal',
      key: 'personal',
      render: (_, record) => record.nombre_completo || '-',
    },
    ...(isMainAdmin ? [{
      title: 'Empresa',
      dataIndex: 'empresa_nombre',
      key: 'empresa_nombre',
    }] : []),
    {
      title: 'Periodo',
      key: 'periodo',
      render: (_, record) => `${record.periodo_mes}/${record.periodo_anio}`,
    },
    {
      title: 'Monto Base',
      dataIndex: 'monto_base',
      key: 'monto_base',
      render: (monto) => `S/ ${parseFloat(monto || 0).toFixed(2)}`,
    },
    {
      title: 'Bonificaciones',
      dataIndex: 'monto_bonificaciones',
      key: 'monto_bonificaciones',
      render: (monto) => `S/ ${parseFloat(monto || 0).toFixed(2)}`,
    },
    {
      title: 'Total',
      dataIndex: 'monto_total',
      key: 'monto_total',
      render: (total) => <strong>S/ {parseFloat(total || 0).toFixed(2)}</strong>,
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      render: (estado) => {
        const colors = {
          pendiente: 'orange',
          liquidado: 'blue',
          pagado: 'green',
          cancelado: 'red',
        };
        return <Tag color={colors[estado]}>{estado === 'cancelado' ? 'ANULADO' : estado.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Fecha Liquidación',
      dataIndex: 'fecha_liquidacion',
      key: 'fecha_liquidacion',
      render: (fecha) => fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<PrinterOutlined />}
            onClick={() => handleVerBoleta(record)}
          >
            Ver Boleta
          </Button>
          {record.estado !== 'cancelado' && (
            <>
              <Button
                type="default"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              >
                Editar
              </Button>
              {record.estado !== 'pagado' && (
                <Popconfirm
                  title="¿Estás seguro de aprobar y cerrar esta liquidación?"
                  description="Esta acción marcará la liquidación como pagada."
                  onConfirm={() => handleAprobar(record)}
                  okText="Sí, Aprobar"
                  cancelText="Cancelar"
                >
                  <Button type="primary" icon={<CheckCircleOutlined />} style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}>
                    Aprobar
                  </Button>
                </Popconfirm>
              )}
            </>
          )}
          {true && (
            <Popconfirm
              title="¿Estás seguro de eliminar esta liquidación?"
              onConfirm={() => handleDelete(record.id)}
              okText="Sí"
              cancelText="No"
            >
              <Button type="primary" danger icon={<DeleteOutlined />}>
                Eliminar
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const meses = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' },
  ];

  return (
    <div>
      <Card
        title="Liquidación de Pagos a Personal"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingLiquidacion(null);
              setDatosCalculo(null);
              setBonificaciones([]);
              form.resetFields();
              form.setFieldsValue({
                periodo_mes: dayjs().month() + 1,
                periodo_anio: dayjs().year(),
                fecha_liquidacion: dayjs(),
              });
              setModalVisible(true);
            }}
          >
            Nueva Liquidación
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={liquidaciones}
          rowKey="id"
          loading={loading}
        />
      </Card>

      <Modal
        title={editingLiquidacion ? 'Editar Liquidación' : 'Nueva Liquidación'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingLiquidacion(null);
          setDatosCalculo(null);
          setBonificaciones([]);
        }}
        footer={null}
        width={800}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="personal_id"
                label="Personal"
                rules={[{ required: true, message: 'Selecciona un personal' }]}
              >
                <Select
                  placeholder="Selecciona un personal"
                  disabled={!!editingLiquidacion}
                  onChange={(value) => {
                    const mes = form.getFieldValue('periodo_mes') || dayjs().month() + 1;
                    const anio = form.getFieldValue('periodo_anio') || dayjs().year();
                    if (value) {
                      handleCalcular(value, mes, anio);
                    }
                  }}
                >
                  {personal.map((p) => (
                    <Option key={p.id} value={p.id}>
                      {p.nombre_completo} - {p.cargo}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="periodo_mes"
                label="Mes"
                rules={[{ required: true, message: 'Selecciona el mes' }]}
              >
                <Select
                  placeholder="Mes"
                  disabled={!!editingLiquidacion}
                  onChange={(value) => {
                    const personalId = form.getFieldValue('personal_id');
                    const anio = form.getFieldValue('periodo_anio') || dayjs().year();
                    if (personalId) {
                      handleCalcular(personalId, value, anio);
                    }
                  }}
                >
                  {meses.map((m) => (
                    <Option key={m.value} value={m.value}>
                      {m.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="periodo_anio"
                label="Año"
                rules={[{ required: true, message: 'Selecciona el año' }]}
              >
                <InputNumber
                  min={2020}
                  max={2100}
                  style={{ width: '100%' }}
                  disabled={!!editingLiquidacion}
                  onChange={(value) => {
                    const personalId = form.getFieldValue('personal_id');
                    const mes = form.getFieldValue('periodo_mes') || dayjs().month() + 1;
                    if (personalId && value) {
                      handleCalcular(personalId, mes, value);
                    }
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          {calculando && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <p>Calculando liquidación...</p>
            </div>
          )}

          {datosCalculo && (
            <Card size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic
                    title="Monto Base"
                    value={datosCalculo.monto_base}
                    prefix={<DollarOutlined />}
                    precision={2}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Servicios"
                    value={datosCalculo.detalles?.length || 0}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Tipo de Pago"
                    value={datosCalculo.personal?.tipo_pago || '-'}
                  />
                </Col>
              </Row>
            </Card>
          )}

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="monto_base"
                label="Monto Base"
                rules={[{ required: true, message: 'Ingresa el monto base' }]}
              >
                <InputNumber min={0} precision={2} style={{ width: '100%' }} disabled />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="monto_bonificaciones"
                label="Total Bonificaciones"
              >
                <InputNumber min={0} precision={2} style={{ width: '100%' }} disabled />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="monto_descuentos"
                label="Descuentos"
              >
                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Bonificaciones Manuales</Divider>
          <Button
            type="dashed"
            onClick={handleAgregarBonificacion}
            style={{ width: '100%', marginBottom: 16 }}
          >
            Agregar Bonificación
          </Button>

          {bonificaciones.map((bonif, index) => (
            <Row gutter={16} key={index} style={{ marginBottom: 8 }}>
              <Col span={16}>
                <Select
                  placeholder="Selecciona una bonificación"
                  value={bonif.descripcion}
                  onChange={(value) => handleBonificacionChange(index, 'descripcion', value)}
                  style={{ width: '100%' }}
                >
                  {TIPOS_BONIFICACION.map((tipo) => (
                    <Option key={tipo} value={tipo}>
                      {tipo}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col span={6}>
                <InputNumber
                  placeholder="Monto"
                  min={0}
                  precision={2}
                  style={{ width: '100%' }}
                  value={bonif.monto}
                  onChange={(value) => handleBonificacionChange(index, 'monto', value)}
                />
              </Col>
              <Col span={2}>
                <Button
                  type="primary"
                  danger
                  onClick={() => handleEliminarBonificacion(index)}
                >
                  X
                </Button>
              </Col>
            </Row>
          ))}

          <Form.Item
            name="fecha_liquidacion"
            label="Fecha de Liquidación"
            rules={[{ required: true, message: 'Selecciona la fecha' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="observaciones" label="Observaciones">
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={calculando}>
                {editingLiquidacion ? 'Actualizar' : 'Crear Liquidación'}
              </Button>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  form.resetFields();
                  setEditingLiquidacion(null);
                  setDatosCalculo(null);
                  setBonificaciones([]);
                }}
              >
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Boleta de Pago - ${selectedLiquidacion?.numero_boleta || ''}`}
        open={boletaModalVisible}
        onCancel={() => {
          setBoletaModalVisible(false);
          setSelectedLiquidacion(null);
        }}
        footer={[
          <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={() => window.print()}>
            Imprimir
          </Button>,
          <Button key="close" onClick={() => {
            setBoletaModalVisible(false);
            setSelectedLiquidacion(null);
          }}>
            Cerrar
          </Button>,
        ]}
        width={800}
      >
        {selectedLiquidacion && (
          <div>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Número de Boleta">
                {selectedLiquidacion.numero_boleta}
              </Descriptions.Item>
              <Descriptions.Item label="Fecha de Liquidación">
                {dayjs(selectedLiquidacion.fecha_liquidacion).format('DD/MM/YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label="Personal">
                {selectedLiquidacion.nombre_completo}
              </Descriptions.Item>
              <Descriptions.Item label="Documento">
                {selectedLiquidacion.documento}
              </Descriptions.Item>
              <Descriptions.Item label="Cargo">
                {selectedLiquidacion.cargo}
              </Descriptions.Item>
              <Descriptions.Item label="Periodo">
                {selectedLiquidacion.periodo_mes}/{selectedLiquidacion.periodo_anio}
              </Descriptions.Item>
            </Descriptions>

            <Divider>Detalles de Servicios</Divider>
            <List
              dataSource={selectedLiquidacion.detalles || []}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    title={item.tipo_servicio || `Servicio #${item.servicio_id}`}
                    description={`${item.lugar_contacto || ''} - ${dayjs(item.fecha_solicitud).format('DD/MM/YYYY')}`}
                  />
                  <div>S/ {parseFloat(item.monto || 0).toFixed(2)}</div>
                </List.Item>
              )}
            />

            {selectedLiquidacion.bonificaciones && selectedLiquidacion.bonificaciones.length > 0 && (
              <>
                <Divider>Bonificaciones</Divider>
                <List
                  dataSource={selectedLiquidacion.bonificaciones}
                  renderItem={(item) => (
                    <List.Item>
                      <List.Item.Meta title={item.descripcion} />
                      <div>S/ {parseFloat(item.monto || 0).toFixed(2)}</div>
                    </List.Item>
                  )}
                />
              </>
            )}

            <Divider />
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Monto Base">
                S/ {parseFloat(selectedLiquidacion.monto_base || 0).toFixed(2)}
              </Descriptions.Item>
              <Descriptions.Item label="Bonificaciones">
                S/ {parseFloat(selectedLiquidacion.monto_bonificaciones || 0).toFixed(2)}
              </Descriptions.Item>
              <Descriptions.Item label="Descuentos">
                S/ {parseFloat(selectedLiquidacion.monto_descuentos || 0).toFixed(2)}
              </Descriptions.Item>
              <Descriptions.Item label="Total a Pagar">
                <strong>S/ {parseFloat(selectedLiquidacion.monto_total || 0).toFixed(2)}</strong>
              </Descriptions.Item>
            </Descriptions>

            {selectedLiquidacion.observaciones && (
              <>
                <Divider>Observaciones</Divider>
                <p>{selectedLiquidacion.observaciones}</p>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default LiquidacionPagos;

